import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { getDestinations, getDestinationsPage } from '../utilities/destinations-api';
import { getExperiences, getExperiencesPage } from '../utilities/experiences-api';
import { showExperience } from '../utilities/experiences-api';
import { getUserPlans } from '../utilities/plans-api';
import { useUser } from './UserContext';
import { logger } from '../utilities/logger';
import { eventBus } from '../utilities/event-bus';
import { useRevalidation, REVALIDATION_CONFIG } from '../hooks/useRevalidation';

logger.debug('DataContext module loaded');

// Preserve context reference across HMR to prevent "must be used within Provider" errors
const _hmrHot = import.meta.hot;
const DataContext = (_hmrHot?.data?.DataContext) || createContext();
if (_hmrHot) {
  _hmrHot.data.DataContext = DataContext;
}

/**
 * Hook to access global data state
 * @returns {Object} Data context with destinations, experiences, plans, and refresh functions
 */
export function useData() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}

/**
 * Data Provider Component
 * Centralized state management for destinations, experiences, and plans
 * Provides instant UI updates across all components when data changes
 * Integrates with UserContext for user-specific data filtering
 */
export function DataProvider({ children }) {
  logger.debug('DataProvider function called');
  const { user, isAuthenticated } = useUser();
  const [destinations, setDestinations] = useState([]);
  const [destinationsShuffled, setDestinationsShuffled] = useState([]);
  const [destinationsFilters, setDestinationsFilters] = useState({});
  const [destinationsOptions, setDestinationsOptions] = useState({});
  const [experiences, setExperiences] = useState([]);
  const [destinationsMeta, setDestinationsMeta] = useState({ page: 0, limit: 30, total: 0, totalPages: 0, hasMore: true });
  const [experiencesMeta, setExperiencesMeta] = useState({ page: 0, limit: 30, total: 0, totalPages: 0, hasMore: true });
  const [experiencesFilters, setExperiencesFilters] = useState({});
  const experiencesFiltersRef = React.useRef(experiencesFilters);
  // Refs to access current data without triggering callback recreation
  const destinationsRef = React.useRef(destinations);
  const destinationsShuffledRef = React.useRef(destinationsShuffled);
  const experiencesRef = React.useRef(experiences);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  // Indicates a background refresh is in progress (cached data already shown)
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState({
    destinations: null,
    experiences: null,
    plans: null,
  });
  // Track optimistic plan state per-experience so canonical updates can avoid
  // stomping freshly applied optimistic UI. Shape: { [experienceId]: { state: Object, at: number } }
  const [optimisticPlanState, setOptimisticPlanState] = useState({});
  // Track the most recent immediate-set of experiences (stale-while-revalidate)
  const [immediateExperiences, setImmediateExperiences] = useState(null);
  // Safe shallow merge helper: copy defined keys from incoming onto existing.
  // This avoids overwriting existing nested objects with `undefined` from
  // partial or inconsistent API payloads (observed with destination fields).
  // Also preserves populated arrays (objects) when incoming has unpopulated (strings).
  const safeMergeObj = useCallback((existing = {}, incoming = {}) => {
    if (!existing) return { ...incoming };
    if (!incoming) return { ...existing };
    const merged = { ...existing };
    Object.keys(incoming).forEach(key => {
      const val = incoming[key];
      if (typeof val === 'undefined') return; // preserve existing value

      // Smart array merge: preserve populated data when incoming is unpopulated
      if (Array.isArray(val) && Array.isArray(existing[key])) {
        const existingArr = existing[key];
        // If existing has populated objects (with .url for photos) but incoming is unpopulated, keep existing
        // Unpopulated means: array of strings OR array of objects without .url property
        if (
          existingArr.length > 0 &&
          typeof existingArr[0] === 'object' &&
          existingArr[0] !== null &&
          existingArr[0].url && // existing has populated photos with URLs
          val.length > 0 &&
          (typeof val[0] === 'string' || (typeof val[0] === 'object' && !val[0]?.url))
        ) {
          return; // preserve existing populated array
        }
      }

      merged[key] = val;
    });
    return merged;
  }, []);

  /**
   * Smart merge for plans: merge canonical data immediately while preserving
   * optimistic flags. This eliminates the need for setTimeout delays.
   * PRINCIPLE: Where there was data, never show empty state. Always merge.
   * @param {Object} existing - Existing plan (may be optimistic)
   * @param {Object} canonical - Canonical data from server
   * @returns {Object} Merged plan with canonical data and cleared optimistic flag
   */
  const mergePlanWithCanonical = useCallback((existing, canonical) => {
    if (!existing) return canonical;
    if (!canonical) return existing; // NEVER return null if we have existing data

    // Merge canonical over existing, preserving any fields not in canonical
    const merged = { ...existing, ...canonical };

    // Preserve local-only tracking fields
    merged.__ctx_merged_at = Date.now();

    // Clear optimistic flag since we now have canonical data
    delete merged._optimistic;

    return merged;
  }, []);

  /**
   * Fetch all destinations from API
   * @param {Object} options - Options for fetching
   * @param {boolean} options.shuffle - Whether to shuffle results on backend
   * @returns {Promise<Array>} Array of destinations
   */
  const fetchDestinations = useCallback(async (options = {}) => {
    // Merge passed options with current options to maintain state
    const mergedOptions = { ...destinationsOptions, ...options };
    const { shuffle = false } = mergedOptions;
    logger.debug('fetchDestinations called', { user: user ? user.email : 'null', shuffle, options, mergedOptions });

    try {
      logger.debug('Calling getDestinations API (page=1)');
      const resp = await getDestinations(destinationsFilters, { shuffle });
      // resp should be object with data/meta from API
      if (resp && resp.data && resp.meta) {
        // Don't set state here - let caller handle it based on shuffle option
        setDestinationsMeta(resp.meta);
        setLastUpdated(prev => ({ ...prev, destinations: new Date() }));
        return resp.data || [];
      } else if (Array.isArray(resp)) {
        // Backwards compatibility: if API returns array, treat as single page
        // Don't set state here - let caller handle it based on shuffle option
        setDestinationsMeta({ page: 1, limit: resp.length, total: resp.length, totalPages: 1, hasMore: false });
        setLastUpdated(prev => ({ ...prev, destinations: new Date() }));
        return resp || [];
      } else {
        logger.warn('Unexpected destinations response format', { resp });
        return [];
      }
    } catch (error) {
      logger.error('Failed to fetch destinations', { error: error.message });
      return [];
    }
  }, [destinationsFilters, destinationsOptions]);

  // Keep a ref with the latest experiencesFilters to avoid stale closures in
  // async fetch functions. This lets fetchMoreExperiences and fetchExperiences
  // always read the most recent filters/data without requiring them in dependency
  // arrays (which could otherwise trigger re-creations and unexpected effects).
  useEffect(() => {
    experiencesFiltersRef.current = experiencesFilters;
  }, [experiencesFilters]);

  useEffect(() => {
    destinationsRef.current = destinations;
  }, [destinations]);

  useEffect(() => {
    destinationsShuffledRef.current = destinationsShuffled;
  }, [destinationsShuffled]);

  useEffect(() => {
    experiencesRef.current = experiences;
  }, [experiences]);

  const applyDestinationsFilter = useCallback(async (filters = {}, options = {}) => {
    const cleanFilters = filters || {};
    const isEmptyFilter = Object.keys(cleanFilters).length === 0;
    const { shuffle = false } = options;

    // Use different state based on shuffle option
    const currentDestinations = shuffle ? destinationsShuffledRef.current : destinationsRef.current;
    const hasExistingData = currentDestinations.length > 0;

    // If applying empty filter and data already exists, skip the refresh
    // This prevents unnecessary flash/re-render when navigating back to home
    if (isEmptyFilter && hasExistingData && !options.forceRefresh) {
      logger.debug('applyDestinationsFilter: Empty filter with existing data, skipping refresh', { shuffle });
      setDestinationsFilters(cleanFilters);
      // Still update options even if we skip the fetch
      setDestinationsOptions(options);
      return currentDestinations;
    }

    setDestinationsFilters(cleanFilters);
    setDestinationsOptions(options);

    // Set loading state BEFORE clearing destinations
    // This prevents "No destinations" flash while filter is being applied
    setLoading(true);

    // Only clear data if we're actually changing filters
    if (!isEmptyFilter) {
      if (shuffle) {
        setDestinationsShuffled([]);
      } else {
        setDestinations([]);
      }
      setDestinationsMeta({ page: 0, limit: 30, total: 0, totalPages: 0, hasMore: true });
    }

    try {
      const result = await fetchDestinations(options);
      // Store results in appropriate state based on shuffle option
      if (shuffle) {
        setDestinationsShuffled(result);
      } else {
        setDestinations(result);
      }
      return result;
    } finally {
      // Clear loading state after filter is applied
      setLoading(false);
    }
  }, [fetchDestinations]);

  /**
   * Fetch all experiences from API
   * @returns {Promise<Array>} Array of experiences
   */
  const fetchExperiences = useCallback(async (filters = {}) => {

    // Use provided filters or the latest filters from ref
    const appliedFilters = Object.keys(filters).length ? filters : (experiencesFiltersRef.current || {});

    try {
      // If caller passed explicit filters, treat this as a new query and reset state
      if (Object.keys(filters).length) {
        setExperiences([]);
        setExperiencesMeta({ page: 0, limit: 30, total: 0, totalPages: 0, hasMore: true });
      }

      const resp = await getExperiences(appliedFilters);

      // resp should be object with data/meta from API
      if (resp && resp.data && resp.meta) {
        setExperiences(resp.data || []);
        setExperiencesMeta(resp.meta);
        setLastUpdated(prev => ({ ...prev, experiences: new Date() }));
        return resp.data || [];
      } else if (Array.isArray(resp)) {
        // Backwards compatibility: if API returns array, treat as single page
        setExperiences(resp || []);
        setExperiencesMeta({ page: 1, limit: resp.length, total: resp.length, totalPages: 1, hasMore: false });
        setLastUpdated(prev => ({ ...prev, experiences: new Date() }));
        return resp || [];
      } else {
        logger.warn('Unexpected experiences response format', { resp });
        return [];
      }
    } catch (error) {
      logger.error('Failed to fetch experiences', { error: error.message });
      return [];
    }
  }, []);

  /**
   * Immediately set experiences in context from provided data (stale-while-revalidate)
   * Shows the provided items to the UI synchronously, then triggers a background
   * refresh from the API to replace with fresh results for the same filters.
   * @param {Array} items - Array of experience objects to show immediately
   * @param {Object} options - Optional params: { meta, filters, backgroundRefresh }
   */
  const setExperiencesImmediate = useCallback((items = [], options = {}) => {
  const { meta = null, filters = null, backgroundRefresh = true } = options || {};
    try {
      // Synchronously show provided items so navigating views render instantly
      setExperiences(Array.isArray(items) ? items : (items && items.data ? items.data : []));
      if (meta) {
        setExperiencesMeta(meta);
      } else if (Array.isArray(items)) {
        // If caller provided items but no meta, set a reasonable meta so
        // pagination / infinite-scroll logic can operate (page=1). We
        // default hasMore to false since we don't know total; callers
        // may override by passing meta.
        const len = items.length;
        if (len > 0) {
          setExperiencesMeta(prev => ({ ...prev, page: 1, limit: prev.limit || len, total: len, totalPages: 1, hasMore: false }));
        }
      }
      if (filters) setExperiencesFilters(filters || {});
      setLastUpdated(prev => ({ ...prev, experiences: new Date() }));

      if (!backgroundRefresh) {
        // still record immediate marker without a refresh
        // normalize filters to an object so consumers can compare reliably
        const normalizedFilters = filters ?? {};
        // Update filters in context immediately so other helpers (fetchMore) can use them
        setExperiencesFilters(normalizedFilters);
        experiencesFiltersRef.current = normalizedFilters;
        setImmediateExperiences({ filters: normalizedFilters, at: Date.now(), status: 'done', promise: Promise.resolve([]) });
        return Promise.resolve([]);
      }

      // Create a refresh promise so callers can deterministically wait for the
      // canonical server refresh to complete. We record the promise in the
      // immediateExperiences marker so views can inspect status or await it.
      const at = Date.now();
      let resolveFn;
      let rejectFn;
      const refreshPromise = new Promise((resolve, reject) => {
        resolveFn = resolve;
        rejectFn = reject;
      });

      // set marker as refreshing
  // Normalize filters to object ({} when null) so views can compare via JSON
  const normalizedFilters = filters ?? {};
  // Update filters in context immediately so other helpers (fetchMore) can use them
  setExperiencesFilters(normalizedFilters);
  experiencesFiltersRef.current = normalizedFilters;
  setImmediateExperiences({ filters: normalizedFilters, at, status: 'refreshing', promise: refreshPromise });

      // Run the refresh (do not block the caller). Use fetchExperiences to keep
      // canonical fetch logic centralized.
      (async () => {
        try {
          // If caller set a special __noApiParam flag, avoid passing those filters to the API
          const apiFilters = (filters && filters.__noApiParam) ? {} : (filters || experiencesFiltersRef.current || {});
          const resp = await fetchExperiences(apiFilters);
          // mark done
          setImmediateExperiences({ filters: normalizedFilters, at, status: 'done', promise: refreshPromise });
          resolveFn(resp || []);
        } catch (err) {
          setImmediateExperiences({ filters: normalizedFilters, at, status: 'error', promise: refreshPromise });
          logger.debug('Background refresh of experiences failed', { error: err?.message || err });
          rejectFn(err);
        }
      })();

      return refreshPromise;
    } catch (err) {
      logger.error('setExperiencesImmediate failed', { error: err?.message || err });
      return Promise.reject(err);
    }
  }, [fetchExperiences]);

  // Fetch next page of destinations and append to state
  const fetchMoreDestinations = useCallback(async () => {
    // If initial page hasn't been loaded yet, don't fetch more
    if (!destinationsMeta || destinationsMeta.page < 1) {
      logger.debug('fetchMoreDestinations: waiting for initial page', { meta: destinationsMeta });
      return [];
    }
    // Check if there are more pages available
    if (!destinationsMeta.hasMore) {
      logger.debug('fetchMoreDestinations: no more pages', { meta: destinationsMeta });
      return [];
    }

    const nextPage = destinationsMeta.page + 1;
    logger.debug('fetchMoreDestinations: fetching page', { nextPage, currentPage: destinationsMeta.page });

    try {
      const resp = await getDestinationsPage(nextPage, destinationsMeta.limit || 30, destinationsFilters);
      if (resp && resp.data && resp.meta) {
        setDestinations(prev => [...prev, ...resp.data]);
        setDestinationsMeta(resp.meta);
        setLastUpdated(prev => ({ ...prev, destinations: new Date() }));
        logger.debug('fetchMoreDestinations: success', { newItems: resp.data.length, newMeta: resp.meta });
        return resp.data;
      }
      return [];
    } catch (err) {
      logger.error('Failed to fetch more destinations', { error: err.message, nextPage });
      return [];
    }
  }, [destinationsMeta, destinationsFilters]);

  // Fetch next page of experiences and append to state
  const fetchMoreExperiences = useCallback(async (filters = null) => {
    const appliedFilters = filters || experiencesFiltersRef.current || {};
    // If initial page hasn't been loaded yet, don't fetch more (prevents requesting page=2 before page=1)
    if (!experiencesMeta || experiencesMeta.page < 1) {
      logger.debug('fetchMoreExperiences: waiting for initial page', { meta: experiencesMeta });
      return [];
    }
    // Check if there are more pages available
    if (!experiencesMeta.hasMore) {
      logger.debug('fetchMoreExperiences: no more pages', { meta: experiencesMeta });
      return [];
    }

    const nextPage = experiencesMeta.page + 1;
    logger.debug('fetchMoreExperiences: fetching page', { nextPage, currentPage: experiencesMeta.page });

    try {
      const resp = await getExperiencesPage(nextPage, experiencesMeta.limit || 30, appliedFilters);
      if (resp && resp.data && resp.meta) {
        setExperiences(prev => [...prev, ...resp.data]);
        setExperiencesMeta(resp.meta);
        setLastUpdated(prev => ({ ...prev, experiences: new Date() }));
        logger.debug('fetchMoreExperiences: success', { newItems: resp.data.length, newMeta: resp.meta });
        return resp.data;
      }
      return [];
    } catch (err) {
      logger.error('Failed to fetch more experiences', { error: err.message, nextPage });
      return [];
    }
  }, [experiencesMeta]);

  /**
   * Fetch all user plans from API
   * @returns {Promise<Array>} Array of plans
   */
  const fetchPlans = useCallback(async () => {
    if (!user) return [];

    try {
      const data = await getUserPlans();
      // Merge canonical plans with any optimistic plan state to avoid
      // stomping optimistic UI entries (temp plans) returned by the client.
      let merged = Array.isArray(data) ? data.slice() : (data || []);
      try {
        Object.entries(optimisticPlanState || {}).forEach(([expId, entry]) => {
          const opt = entry && entry.state ? entry.state : null;
          if (!opt || !opt.userPlan) return;
          const up = opt.userPlan;
          // If optimistic userPlan already present in merged, replace it with optimistic copy
          const idx = merged.findIndex(p => p._id && up._id && p._id.toString() === up._id.toString());
          if (idx >= 0) {
            merged[idx] = { ...merged[idx], ...up };
          } else {
            // Ensure optimistic plan appears in the canonical list so UI keeps showing it
            merged = [up, ...merged];
          }
        });
      } catch (err) {
        logger.debug('Failed to merge optimistic plans into fetched plans', { error: err?.message });
      }
      setPlans(merged || []);
      setLastUpdated(prev => ({ ...prev, plans: new Date() }));
      return data || [];
    } catch (error) {
      logger.error('Failed to fetch plans', { error: error.message });
      return [];
    }
  }, [user, optimisticPlanState]);

  /**
   * Refresh all data (destinations, experiences, and plans)
   * @param {Object} options - Options for selective refresh
   * @param {boolean} options.destinations - Refresh destinations
   * @param {boolean} options.experiences - Refresh experiences
   * @param {boolean} options.plans - Refresh plans
   * @returns {Promise<void>}
   */
  const refreshAll = useCallback(async (options = {}) => {
    if (!user) {
      logger.debug('refreshAll called but no user, returning early');
      return;
    }

    logger.debug('refreshAll called', { user: user.email, options });

    const {
      destinations: refreshDestinations = true,
      experiences: refreshExperiences = true,
      plans: refreshPlans = true,
    } = options;

    // Only set loading=true if we don't have any existing data
    // This prevents flash/re-render when data already exists (background refresh)
    const hasExistingData = destinationsRef.current.length > 0 || experiencesRef.current.length > 0;
    if (!hasExistingData) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      const promises = [];
      const promiseLabels = [];

      if (refreshDestinations) {
        logger.debug('Adding fetchDestinations to promises');
        promises.push(fetchDestinations(options));
        promiseLabels.push('destinations');
      }
      if (refreshExperiences) {
        logger.debug('Adding fetchExperiences to promises');
        promises.push(fetchExperiences());
        promiseLabels.push('experiences');
      }
      if (refreshPlans) {
        logger.debug('Adding fetchPlans to promises');
        promises.push(fetchPlans());
        promiseLabels.push('plans');
      }

      logger.debug('Executing fetch promises', { count: promises.length });
      const results = await Promise.all(promises);
      logger.debug('refreshAll completed', { results: results.map(r => r?.length || 0) });

      // fetchDestinations no longer sets state internally — store result here
      const destIndex = promiseLabels.indexOf('destinations');
      if (destIndex !== -1 && results[destIndex]) {
        const shuffle = options?.shuffle ?? false;
        if (shuffle) {
          setDestinationsShuffled(results[destIndex]);
        } else {
          setDestinations(results[destIndex]);
        }
      }

    } catch (error) {
      logger.error('Failed to refresh data', { error: error.message });
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [user, fetchDestinations, fetchExperiences, fetchPlans]);

  /**
   * Update a single destination in state (optimistic update)
   * Uses upsert pattern: adds if not found, updates if exists
   * Uses safeMergeObj to preserve populated arrays (e.g., photos)
   * @param {Object} updatedDestination - Full updated destination object
   */
  const updateDestination = useCallback((updatedDestination) => {
    setDestinations(prev => {
      const existingIndex = prev.findIndex(dest => dest._id === updatedDestination._id);
      if (existingIndex >= 0) {
        // Destination exists - merge carefully to preserve populated data
        return prev.map(dest =>
          dest._id === updatedDestination._id ? safeMergeObj(dest, updatedDestination) : dest
        );
      } else {
        // Destination doesn't exist - add it
        return [...prev, updatedDestination];
      }
    });
    setLastUpdated(prev => ({ ...prev, destinations: new Date() }));
  }, [safeMergeObj]);

  /**
   * Add a new destination to state (optimistic update)
   * @param {Object} destination - New destination object
   */
  const addDestination = useCallback((destination) => {
    setDestinations(prev => [...prev, destination]);
    setLastUpdated(prev => ({ ...prev, destinations: new Date() }));
  }, []);

  /**
   * Remove a destination from state (optimistic update)
   * @param {string} destinationId - Destination ID to remove
   */
  const removeDestination = useCallback((destinationId) => {
    setDestinations(prev => prev.filter(dest => dest._id !== destinationId));
    setLastUpdated(prev => ({ ...prev, destinations: new Date() }));
  }, []);

  /**
   * Update a single experience in state (optimistic update)
   * Uses upsert pattern: adds if not found, updates if exists
   * @param {Object} updatedExperience - Full updated experience object
   */
  const updateExperience = useCallback((updatedExperience) => {
    setExperiences(prev => {
      const existingIndex = prev.findIndex(exp => exp._id === updatedExperience._id);
      if (existingIndex >= 0) {
        // Experience exists - merge carefully and preserve local optimistic markers
        return prev.map(exp =>
          exp._id === updatedExperience._id ? { ...safeMergeObj(exp, updatedExperience), __ctx_merged_at: Date.now() } : exp
        );
      } else {
        // Experience doesn't exist - add it
        return [...prev, { ...updatedExperience, __ctx_merged_at: Date.now() }];
      }
    });
    setLastUpdated(prev => ({ ...prev, experiences: new Date() }));
  }, []);

  const setOptimisticPlanStateForExperience = useCallback((experienceId, state) => {
    try {
      if (!experienceId) return;
      setOptimisticPlanState(prev => ({ ...prev, [experienceId]: { state, at: Date.now() } }));
      logger.debug('[DataContext] setOptimisticPlanStateForExperience', { experienceId, state });
    } catch (err) {
      logger.warn('setOptimisticPlanStateForExperience failed', { error: err?.message });
    }
  }, []);

  const clearOptimisticPlanStateForExperience = useCallback((experienceId) => {
    try {
      if (!experienceId) return;
      setOptimisticPlanState(prev => {
        const copy = { ...prev };
        delete copy[experienceId];
        return copy;
      });
      logger.debug('[DataContext] clearOptimisticPlanStateForExperience', { experienceId });
    } catch (err) {
      logger.warn('clearOptimisticPlanStateForExperience failed', { error: err?.message });
    }
  }, []);

  /**
   * Add a new experience to state (optimistic update)
   * @param {Object} experience - New experience object
   */
  const addExperience = useCallback((experience) => {
    setExperiences(prev => [...prev, experience]);
    setLastUpdated(prev => ({ ...prev, experiences: new Date() }));
  }, []);

  /**
   * Remove an experience from state (optimistic update)
   * @param {string} experienceId - Experience ID to remove
   */
  const removeExperience = useCallback((experienceId) => {
    setExperiences(prev => prev.filter(exp => exp._id !== experienceId));
    setLastUpdated(prev => ({ ...prev, experiences: new Date() }));
  }, []);

  /**
   * Update a single plan in state (optimistic update)
   * Uses upsert pattern: adds if not found, updates if exists
   * @param {string} planId - Plan ID
   * @param {Object} updates - Updated plan data (or full plan object)
   */
  const updatePlan = useCallback((planId, updates) => {
    setPlans(prev => {
      const existingIndex = prev.findIndex(plan => plan._id === planId);
      if (existingIndex >= 0) {
        // Plan exists - update it
        return prev.map(plan =>
          plan._id === planId ? { ...plan, ...updates } : plan
        );
      } else {
        // Plan doesn't exist - add it (if updates is a full plan object)
        if (updates._id || planId) {
          const newPlan = updates._id ? updates : { _id: planId, ...updates };
          return [...prev, newPlan];
        }
        return prev;
      }
    });
    setLastUpdated(prev => ({ ...prev, plans: new Date() }));
  }, []);

  /**
   * Add a new plan to state (optimistic update)
   * @param {Object} plan - New plan object
   */
  const addPlan = useCallback((plan) => {
    setPlans(prev => [...prev, plan]);
    setLastUpdated(prev => ({ ...prev, plans: new Date() }));
  }, []);

  /**
   * Remove a plan from state (optimistic update)
   * @param {string} planId - Plan ID to remove
   */
  const removePlan = useCallback((planId) => {
    setPlans(prev => prev.filter(plan => plan._id !== planId));
    setLastUpdated(prev => ({ ...prev, plans: new Date() }));
  }, []);

  /**
   * Get a specific destination by ID
   * @param {string} destinationId - Destination ID
   * @returns {Object|null} Destination object or null
   */
  const getDestination = useCallback((destinationId) => {
    return destinations.find(dest => dest._id === destinationId) || null;
  }, [destinations]);

  /**
   * Get a specific experience by ID
   * @param {string} experienceId - Experience ID
   * @returns {Object|null} Experience object or null
   */
  const getExperience = useCallback((experienceId) => {
    return experiences.find(exp => exp._id === experienceId) || null;
  }, [experiences]);

  /**
   * Get a specific plan by ID
   * @param {string} planId - Plan ID
   * @returns {Object|null} Plan object or null
   */
  const getPlan = useCallback((planId) => {
    return plans.find(plan => plan._id === planId) || null;
  }, [plans]);

  /**
   * Get user's plan for a specific experience
   * @param {string} experienceId - Experience ID
   * @returns {Object|null} Plan object or null
   */
  const getUserPlanForExperience = useCallback((experienceId) => {
    return plans.find(plan => plan.experience === experienceId || plan.experience._id === experienceId) || null;
  }, [plans]);

  // Apply filters for experiences (sets filters and fetches first page)
  // NOTE: keep this function identity stable and avoid depending on fetchExperiences
  // to prevent causing effects that depend on this function to re-run when
  // internal fetch functions change identity (which can create render loops).
  const applyExperiencesFilter = useCallback(async (filters = {}) => {
    const cleanFilters = filters || {};
    const isEmptyFilter = Object.keys(cleanFilters).length === 0;
    const currentExperiences = experiencesRef.current;
    const hasExistingData = currentExperiences.length > 0;

    // If applying empty filter and data already exists, skip the refresh
    // This prevents unnecessary flash/re-render when navigating back to home
    if (isEmptyFilter && hasExistingData) {
      logger.debug('applyExperiencesFilter: Empty filter with existing data, skipping refresh');
      setExperiencesFilters(cleanFilters);
      return currentExperiences;
    }

    setExperiencesFilters(cleanFilters);
    logger.info('applyExperiencesFilter called', {
      filters: cleanFilters,
      hasViewSpecific: cleanFilters.__viewSpecific,
      isEmpty: isEmptyFilter
    });

    // Set loading state BEFORE clearing experiences
    // This prevents "No experiences" flash while filter is being applied
    setLoading(true);

    // Only clear data if we're actually changing filters
    if (!isEmptyFilter) {
      setExperiences([]);
      setExperiencesMeta({ page: 0, limit: 30, total: 0, totalPages: 0, hasMore: true });
    }

    try {
      // Call the API directly rather than delegating to fetchExperiences
      const resp = await getExperiences(cleanFilters);

      // resp should be object with data/meta from API
      if (resp && resp.data && resp.meta) {
        setExperiences(resp.data || []);
        setExperiencesMeta(resp.meta);
        setLastUpdated(prev => ({ ...prev, experiences: new Date() }));
        return resp.data || [];
      } else if (Array.isArray(resp)) {
        // Backwards compatibility: if API returns array, treat as single page
        setExperiences(resp || []);
        setExperiencesMeta({ page: 1, limit: resp.length, total: resp.length, totalPages: 1, hasMore: false });
        setLastUpdated(prev => ({ ...prev, experiences: new Date() }));
        return resp || [];
      } else {
        logger.warn('Unexpected experiences filter response format', { resp });
        return [];
      }
    } catch (error) {
      logger.error('Failed to apply experiences filter', { error: error.message });
      return [];
    } finally {
      // Clear loading state after filter is applied
      setLoading(false);
    }
  }, []);

  // Initial data fetch on mount or when user changes
  useEffect(() => {
    logger.debug('DataContext useEffect triggered', {
      isAuthenticated,
      user: user ? { email: user.email, _id: user._id } : null,
      userId: user?._id
    });
    if (isAuthenticated && user) {
      logger.debug('Calling refreshAll() for user', { user: user.email });
      // Don't fetch destinations initially - let AppHome handle it with shuffle
      refreshAll({ destinations: false });
    } else {
      logger.debug('Clearing data - user not authenticated or user is null');
      // Clear data when user logs out
      setDestinations([]);
      setExperiences([]);
      setPlans([]);
    }
  }, [isAuthenticated, user, refreshAll]); // Include refreshAll to satisfy linting

  // Listen for global plan lifecycle events (created/updated/deleted) and
  // update DataContext plans so all consumers reflect changes immediately.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onPlanCreated = (e) => {
      try {
        const plan = e?.plan || e?.data;
        if (!plan || !plan._id) return;

        const rawExp = e?.experienceId || plan?.experience?._id || plan?.experience || null;
        const expId = rawExp && rawExp.toString ? rawExp.toString() : rawExp;

        // BATCH all state updates to prevent UI flashing (single re-render)
        batchedUpdates(() => {
          // IMMEDIATE MERGE: Apply canonical data right away, merging with any optimistic state
          // No more setTimeout delays - merge preserves existing data while updating with canonical
          setPlans(prev => {
            const existingIndex = prev.findIndex(p => p._id && p._id.toString() === plan._id.toString());
            if (existingIndex >= 0) {
              // Merge canonical with existing (may be optimistic)
              return prev.map((p, idx) =>
                idx === existingIndex ? mergePlanWithCanonical(p, plan) : p
              );
            }
            // Check if there's an optimistic plan with temp ID that should be replaced
            const optimisticIndex = prev.findIndex(p =>
              p._optimistic && p.experience?.toString() === expId
            );
            if (optimisticIndex >= 0) {
              // Replace optimistic with canonical
              return prev.map((p, idx) =>
                idx === optimisticIndex ? mergePlanWithCanonical(p, plan) : p
              );
            }
            // New plan - add to list
            return [plan, ...prev];
          });
          setLastUpdated(prev => ({ ...prev, plans: new Date() }));
          // Clear optimistic marker immediately
          if (expId) {
            setOptimisticPlanState(prev => { const c = { ...prev }; delete c[expId]; return c; });
          }
        });

        logger.debug('[DataContext] plan:created applied immediately via merge', { planId: plan._id, expId });

        // Also refresh the related experience in context so views that read
        // from DataContext don't get a stale experience after a plan change.
        // IMMEDIATE MERGE - no setTimeout delays
        (async () => {
          try {
            if (!expId) return;
            const updatedExp = await showExperience(expId);
            if (!updatedExp) return;

            logger.debug('[DataContext] plan:create -> fetched experience', { expId, fetchedAt: Date.now(), fetchedPreview: { _id: updatedExp._id, plan_items_count: (updatedExp.plan_items || []).length } });

            // IMMEDIATE MERGE: Apply canonical experience data right away
            // Merge preserves existing local state while adding canonical updates
            batchedUpdates(() => {
              setExperiences(prev => {
                const found = prev.findIndex(x => x._id && (x._id.toString ? x._id.toString() === updatedExp._id.toString() : x._id === updatedExp._id));
                if (found >= 0) {
                  // NEVER replace, always merge to preserve local state
                  return prev.map((x, idx) =>
                    idx === found ? { ...safeMergeObj(x, updatedExp), __ctx_merged_at: Date.now() } : x
                  );
                }
                // If experience not in list, add it to front to ensure freshness
                return [{ ...updatedExp, __ctx_merged_at: Date.now() }, ...prev];
              });
              setLastUpdated(prev => ({ ...prev, experiences: new Date() }));
            });
          } catch (err) {
            logger.debug('Failed to refresh experience after plan create', { error: err?.message });
          }
        })();
      } catch (err) {
        logger.warn('DataContext onPlanCreated handler failed', { error: err?.message });
      }
    };

    const onPlanUpdated = (e) => {
      try {
        const plan = e?.plan || e?.data;
        if (!plan || !plan._id) return;

        const rawExp = e?.experienceId || plan?.experience?._id || plan?.experience || null;
        const expId = rawExp && rawExp.toString ? rawExp.toString() : rawExp;

        // BATCH all state updates to prevent UI flashing (single re-render)
        // IMMEDIATE MERGE - no setTimeout delays
        batchedUpdates(() => {
          setPlans(prev => prev.map(p => {
            if (p._id && plan._id && p._id.toString() === plan._id.toString()) {
              // NEVER replace, always merge to preserve local state
              return { ...p, ...plan, __ctx_merged_at: Date.now() };
            }
            return p;
          }));
          setLastUpdated(prev => ({ ...prev, plans: new Date() }));
          if (expId) {
            setOptimisticPlanState(prev => { const c = { ...prev }; delete c[expId]; return c; });
          }
        });

        logger.debug('[DataContext] plan:updated applied immediately via merge', { planId: plan._id, expId });

        // Keep the related experience fresh as well. IMMEDIATE MERGE - no setTimeout delays
        (async () => {
          try {
            if (!expId) return;
            const updatedExp = await showExperience(expId);
            if (!updatedExp) return;

            logger.debug('[DataContext] plan:update -> fetched experience', { expId, fetchedAt: Date.now(), fetchedPreview: { _id: updatedExp._id, plan_items_count: (updatedExp.plan_items || []).length } });

            // IMMEDIATE MERGE: Apply canonical experience data right away
            // Merge preserves existing local state while adding canonical updates
            batchedUpdates(() => {
              setExperiences(prev => prev.map(x => {
                if (x._id && (x._id.toString ? x._id.toString() === updatedExp._id.toString() : x._id === updatedExp._id)) {
                  // NEVER replace, always merge to preserve local state
                  return { ...safeMergeObj(x, updatedExp), __ctx_merged_at: Date.now() };
                }
                return x;
              }));
              setLastUpdated(prev => ({ ...prev, experiences: new Date() }));
            });
          } catch (err) {
            logger.debug('Failed to refresh experience after plan update', { error: err?.message });
          }
        })();
      } catch (err) {
        logger.warn('DataContext onPlanUpdated handler failed', { error: err?.message });
      }
    };

    const onPlanDeleted = (e) => {
      try {
        const plan = e?.plan || e?.data;
        const experienceId = e?.experienceId || (plan && (plan.experience?._id || plan.experience)) || null;
        const expId = experienceId && experienceId.toString ? experienceId.toString() : experienceId;

        // BATCH all state updates to prevent UI flashing (single re-render)
        batchedUpdates(() => {
          if (plan && plan._id) {
            setPlans(prev => prev.filter(p => !(p._id && p._id.toString() === plan._id.toString())));
          } else if (expId) {
            setPlans(prev => prev.filter(p => {
              const pExp = p.experience?._id || p.experience || null;
              return !(pExp && pExp.toString ? pExp.toString() === expId : pExp === expId);
            }));
          }
          setLastUpdated(prev => ({ ...prev, plans: new Date() }));
          if (expId) {
            setOptimisticPlanState(prev => { const c = { ...prev }; delete c[expId]; return c; });
          }
        });

        logger.debug('[DataContext] plan:deleted applied immediately', { planId: plan?._id, expId });

        // Also refresh the experience to remove plan-related flags. IMMEDIATE MERGE - no setTimeout delays
        if (expId) {
          (async () => {
            try {
              const updatedExp = await showExperience(expId);
              if (!updatedExp) return;

              logger.debug('[DataContext] plan:delete -> fetched experience', { expId, fetchedAt: Date.now(), fetchedPreview: { _id: updatedExp._id, plan_items_count: (updatedExp.plan_items || []).length } });

              // IMMEDIATE MERGE: Apply canonical experience data right away
              // Merge preserves existing local state while adding canonical updates
              batchedUpdates(() => {
                setExperiences(prev => prev.map(x => {
                  if (x._id && (x._id.toString ? x._id.toString() === updatedExp._id.toString() : x._id === updatedExp._id)) {
                    // NEVER replace, always merge to preserve local state
                    return { ...safeMergeObj(x, updatedExp), __ctx_merged_at: Date.now() };
                  }
                  return x;
                }));
                setLastUpdated(prev => ({ ...prev, experiences: new Date() }));
              });
            } catch (err) {
              logger.debug('Failed to refresh experience after plan delete', { error: err?.message });
            }
          })();
        }
      } catch (err) {
        logger.warn('DataContext onPlanDeleted handler failed', { error: err?.message });
      }
    };

    // Subscribe to standardized events via event bus
    const unsubscribeCreated = eventBus.subscribe('plan:created', onPlanCreated);
    const unsubscribeUpdated = eventBus.subscribe('plan:updated', onPlanUpdated);
    const unsubscribeDeleted = eventBus.subscribe('plan:deleted', onPlanDeleted);

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeDeleted();
    };
  }, []);

  // Background fetch: populate destinations when never fetched
  // AppHome loads destinationsShuffled but not destinations — this ensures
  // components like Profile that read from destinations get data too.
  useEffect(() => {
    if (!user) return;
    if (destinations.length > 0) return;
    if (lastUpdated.destinations) return;
    if (loading) return;

    logger.debug('DataContext: destinations empty and never fetched, triggering background load');
    fetchDestinations().then(result => {
      if (result && result.length > 0) {
        setDestinations(result);
      }
    }).catch(() => {});
  }, [user, destinations.length, lastUpdated.destinations, loading, fetchDestinations]);

  // Unified stale-while-revalidate: periodic checks, tab visibility, network reconnect
  const { revalidationStatus, markStale, forceRevalidate } = useRevalidation(
    REVALIDATION_CONFIG,
    {
      destinations: async () => {
        const result = await fetchDestinations();
        if (result) setDestinations(result);
      },
      experiences: fetchExperiences,
      plans: fetchPlans,
    },
    {
      user,
      lastUpdated,
      hasData: {
        destinations: destinations.length > 0,
        experiences: experiences.length > 0,
        plans: plans.length > 0,
      },
    }
  );

  // Derive isRefreshing for backward compatibility
  const isRevalidating = Object.values(revalidationStatus).some(s => s === 'revalidating');

  // Event bus listeners - update DataContext when events are broadcast
  // Note: eventBus.subscribe() receives event object with data at top level (e.g., event.destination)
  // while CustomEvent via window.dispatchEvent has data in event.detail (e.g., event.detail.destination)
  // We support both formats for compatibility
  useEffect(() => {
    const handleExperienceUpdated = (event) => {
      const experience = event.experience || event.detail?.experience;
      if (experience && experience._id) {
        logger.debug('[DataContext] experience:updated event received, marking stale', { id: experience._id });
        markStale('experiences');
      }
    };

    const handleExperienceCreated = (event) => {
      const experience = event.experience || event.detail?.experience;
      if (experience && experience._id) {
        logger.debug('[DataContext] experience:created event received, marking stale', { id: experience._id });
        markStale('experiences');
      }
    };

    const handleExperienceDeleted = (event) => {
      const experienceId = event.experienceId || event.detail?.experienceId;
      if (experienceId) {
        logger.debug('[DataContext] experience:deleted event received, marking stale', { id: experienceId });
        markStale('experiences');
      }
    };

    const handleDestinationUpdated = (event) => {
      const destination = event.destination || event.detail?.destination;
      if (destination && destination._id) {
        logger.debug('[DataContext] destination:updated event received, marking stale', { id: destination._id });
        markStale('destinations');
      }
    };

    const handleDestinationCreated = (event) => {
      const destination = event.destination || event.detail?.destination;
      if (destination && destination._id) {
        logger.debug('[DataContext] destination:created event received, marking stale', { id: destination._id });
        markStale('destinations');
      }
    };

    const handleDestinationDeleted = (event) => {
      const destinationId = event.destinationId || event.detail?.destinationId;
      if (destinationId) {
        logger.debug('[DataContext] destination:deleted event received, marking stale', { id: destinationId });
        markStale('destinations');
      }
    };

    const handlePlanUpdated = (event) => {
      // Event payload may have plan data in different locations:
      // - updatePlanItem: { plan, planId }
      // - updatePlan: { data, planId, version }
      // - reorderPlanItems: { data, planId, version }
      // Support both eventBus format (event.plan) and CustomEvent format (event.detail.plan)
      const plan = event.plan || event.data || event.detail?.plan || event.detail?.data;
      const planId = event.planId || event.detail?.planId;
      if (plan || planId) {
        const id = planId || plan?._id;
        logger.debug('[DataContext] plan:updated event received', { id });
        if (plan) {
          updatePlan(id, plan);
        } else {
          // Partial update - refetch plans
          fetchPlans().catch(() => {});
        }
      }
    };

    const handlePlanCreated = (event) => {
      const plan = event.plan || event.detail?.plan;
      if (plan && plan._id) {
        logger.debug('[DataContext] plan:created event received', { id: plan._id });
        addPlan(plan);
      }
    };

    const handlePlanDeleted = (event) => {
      const planId = event.planId || event.detail?.planId;
      if (planId) {
        logger.debug('[DataContext] plan:deleted event received', { id: planId });
        removePlan(planId);
      }
    };

    // Register event listeners using event bus
    const unsubscribeExperienceUpdated = eventBus.subscribe('experience:updated', handleExperienceUpdated);
    const unsubscribeExperienceCreated = eventBus.subscribe('experience:created', handleExperienceCreated);
    const unsubscribeExperienceDeleted = eventBus.subscribe('experience:deleted', handleExperienceDeleted);
    const unsubscribeDestinationUpdated = eventBus.subscribe('destination:updated', handleDestinationUpdated);
    const unsubscribeDestinationCreated = eventBus.subscribe('destination:created', handleDestinationCreated);
    const unsubscribeDestinationDeleted = eventBus.subscribe('destination:deleted', handleDestinationDeleted);
    const unsubscribePlanUpdated = eventBus.subscribe('plan:updated', handlePlanUpdated);
    const unsubscribePlanCreated = eventBus.subscribe('plan:created', handlePlanCreated);
    const unsubscribePlanDeleted = eventBus.subscribe('plan:deleted', handlePlanDeleted);

    // Cleanup listeners on unmount
    return () => {
      unsubscribeExperienceUpdated();
      unsubscribeExperienceCreated();
      unsubscribeExperienceDeleted();
      unsubscribeDestinationUpdated();
      unsubscribeDestinationCreated();
      unsubscribeDestinationDeleted();
      unsubscribePlanUpdated();
      unsubscribePlanCreated();
      unsubscribePlanDeleted();
    };
  }, [updateExperience, addExperience, removeExperience, updateDestination, addDestination, removeDestination, updatePlan, addPlan, removePlan, fetchPlans, markStale]);

  const value = {
    // State
    destinations,
    destinationsShuffled,
    experiences,
    plans,
    loading,
    isRefreshing: isRefreshing || isRevalidating,
    lastUpdated,

    // Fetch functions
    fetchDestinations,
    fetchExperiences,
    fetchPlans,
    refreshAll,
  // Pagination helpers
  fetchMoreDestinations,
  fetchMoreExperiences,
  destinationsMeta,
  experiencesMeta,
  // Filtering helpers
  applyDestinationsFilter,
  destinationsFilters,
  applyExperiencesFilter,
  experiencesFilters,
  // Revalidation controls
  revalidationStatus,
  forceRevalidate,
  // Immediate-set helper (stale-while-revalidate)
  setExperiencesImmediate,
  // Marker for immediate-set (helpers can inspect to avoid overwrite races)
  immediateExperiences,

  // Optimistic plan state helpers (used to preserve optimistic UI during canonical refresh)
  optimisticPlanState,
  setOptimisticPlanStateForExperience,
  clearOptimisticPlanStateForExperience,

    // Getters
    getDestination,
    getExperience,
    getPlan,
    getUserPlanForExperience,

    // Optimistic update functions
    updateDestination,
    addDestination,
    removeDestination,
    updateExperience,
    addExperience,
    removeExperience,
    updatePlan,
    addPlan,
    removePlan,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}
